import React, { useMemo } from 'react';
import { defineMessages, useIntl } from 'react-intl';

import IconButton from '@atlaskit/button/icon/button';
import { cssMap } from '@atlaskit/css';
import Heading from '@atlaskit/heading/heading';
import ArrowLeftIcon from '@atlaskit/icon/core/arrow-left';
import { Box } from '@atlaskit/primitives/compiled/box';
import { Inline } from '@atlaskit/primitives/compiled/inline';
import { token } from '@atlaskit/tokens';

import ConversationAssistant from '@atlassian/conversation-assistant/AsyncConversationAssistant';
import type { UIConfig } from '@atlassian/conversation-assistant/types';

import { useConversationAssistantConfig } from '@confluence/smart-create/entry-points/useConfig.ts';

/**
 * The embedded-chat configuration Jira Admin Agent passes when it opens a Rovodal
 * (`platform/packages/ai-mate/jira-admin-agent/src/ui/jira-admin-agent/JiraAdminAgentRenderer.tsx`).
 * `externalEmbed` drops the sidebar's own margins, border and radius so the chat
 * fills Rovodal's chat card; the disabled elements are the chrome that would
 * either duplicate the modal's top bar or navigate the user out of the modal.
 *
 * `mainChatHeader` stays off even though this column now has a header of its own:
 * Rovo's header would render a second back arrow, and without a `previousView` it
 * would call `resetStateAndCreateConversation` — starting a new chat rather than
 * returning to the connections.
 */
const BASE_UI_CONFIG: Partial<UIConfig> = {
	externalEmbed: true,
	fullScreen: false,
	// Open on a fresh backend-backed conversation (created inside the chat's own store
	// container) instead of the shared EMPTY_CONVERSATION_ID, so the first send performs no
	// EMPTY→real transition that rebuilds the chat and re-seeds the prompt, and each open gets a
	// unique draft slot. The behaviour is experiment-gated inside conversation-assistant.
	createConversationOnMount: { followUpSource: 'teamworkGraphByline' },
	elements: {
		mainChatHeader: false,
		navbar: false,
		closeButton: false,
		fullscreenButton: false,
		newChatButton: false,
		createAgentButton: false,
		browseAgentsButton: false,
		agentsMenu: false,
		stagingAreaEnabled: false,
	},
};

const styles = cssMap({
	root: {
		display: 'flex',
		flexDirection: 'column',
		height: '100%',
		minHeight: '0px',
		overflow: 'hidden',
	},
	header: {
		flexShrink: 0,
		display: 'flex',
		justifyContent: 'space-between',
		alignItems: 'center',
		paddingBlock: token('space.200'),
		paddingInline: token('space.250'),
	},
	chat: {
		flex: '1 1 auto',
		minHeight: '0px',
	},
});

const i18n = defineMessages({
	heading: {
		id: 'byline-connections.preview-panel.modal.rovo-column.heading',
		defaultMessage: 'Rovo',
		description:
			'Heading shown at the top of the Rovo chat column in the Teamwork Graph modal, next to the button that goes back to the connections.',
	},
	backLabel: {
		id: 'byline-connections.preview-panel.modal.rovo-column.back-label',
		defaultMessage: 'Back to connections',
		description:
			'Accessible label for the button at the top of the Rovo chat column that leaves the chat and shows this content’s connections again.',
	},
});

export type TeamworkGraphChatColumnProps = {
	/** Returns the modal's right column to the connection sections. */
	onBack: () => void;
	prefilledPrompt?: string;
};

/**
 * Rovo chat as it appears in the right-hand column of `TeamworkGraphModal`, with the
 * header that leaves it.
 *
 * Any per-link context is attached by the "Appears in" row action, which publishes a
 * `chat-new` rovo-trigger — this column just renders the chat with the base config.
 */
export function TeamworkGraphChatColumn({
	onBack,
	prefilledPrompt,
}: TeamworkGraphChatColumnProps): React.JSX.Element {
	const { formatMessage } = useIntl();
	const config = useConversationAssistantConfig({
		preventAbortRequestsOnStandaloneChatClose: true,
	});
	const uiConfig = useMemo<Partial<UIConfig>>(
		() =>
			prefilledPrompt
				? {
						...BASE_UI_CONFIG,
						initialPromptText: prefilledPrompt,
						// Without this, `initialPromptText` is only applied for users in the
						// `cc_ai_growth_welcome_from_rovo` experiment.
						shouldSeedInitialPromptText: true,
					}
				: BASE_UI_CONFIG,
		[prefilledPrompt],
	);

	return (
		<Box xcss={styles.root} testId="teamwork-graph-modal-rovo-column">
			<Box xcss={styles.header}>
				<Inline alignBlock="center" space="space.100">
					<IconButton
						icon={ArrowLeftIcon}
						label={formatMessage(i18n.backLabel)}
						appearance="subtle"
						spacing="compact"
						shape="circle"
						onClick={onBack}
						testId="teamwork-graph-modal-rovo-back"
					/>
					<Heading size="xsmall">{formatMessage(i18n.heading)}</Heading>
				</Inline>
			</Box>
			<Box xcss={styles.chat}>
				<ConversationAssistant
					testId="teamwork-graph-modal-chat"
					config={config}
					uiConfig={uiConfig}
				/>
			</Box>
		</Box>
	);
}
